/**
 * Command Center - Unified admin landing page
 * Links to all V2 dashboards with live status indicators
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Activity, ArrowRight, Bitcoin, Building2, ChevronRight, Coins,
  Flame, Gauge, Layers, Lock, LogOut, Scale, Shield,
  Sparkles, Timer, TrendingDown, TrendingUp, User, Users, Wallet, Leaf,
  RefreshCw, Banknote, Package,
} from 'lucide-react';
import { getProtocolDashboard, getHalvingStatus } from '@/lib/v2Api';
import { useAuth } from '@/lib/AuthContext';

const fmt = (n) => new Intl.NumberFormat().format(n || 0);

// ─── Module Card ───
const ModuleCard = ({ title, description, href, icon: Icon, stats, accentColor, badge }) => {
  const navigate = useNavigate();
  return (
    <Card
      className="group relative overflow-hidden cursor-pointer card-hover-lift border-transparent hover:border-[hsl(var(--web3-primary))]/30 transition-all duration-300"
      onClick={() => navigate(href)}
      data-testid={`module-${href.replace('/', '')}`}
    >
      <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[radial-gradient(60%_60%_at_80%_20%,${accentColor}_0%,transparent_60%)]`} />
      <CardContent className="relative pt-5 pb-4 px-5 space-y-3">
        <div className="flex items-start justify-between">
          <div className="h-11 w-11 rounded-xl bg-[hsl(var(--web3-surface-2))]/80 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
            <Icon className="h-5.5 w-5.5 text-[hsl(var(--web3-primary))]" />
          </div>
          {badge && (
            <Badge variant="outline" className={`text-[10px] ${badge.color}`}>
              {badge.label}
            </Badge>
          )}
        </div>
        <div>
          <h3 className="text-base heading-font font-semibold tracking-tight group-hover:text-[hsl(var(--web3-primary))] transition-colors">{title}</h3>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
        </div>
        {stats && (
          <div className="grid grid-cols-2 gap-2">
            {stats.map((s) => (
              <div key={s.label} className="text-center py-1.5 rounded-md bg-[hsl(var(--web3-surface-2))]/50">
                <p className="text-lg heading-font font-bold tabular-nums leading-tight">{s.value}</p>
                <p className="text-[9px] text-muted-foreground uppercase tracking-wide">{s.label}</p>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center text-xs text-muted-foreground group-hover:text-[hsl(var(--web3-primary))] transition-colors">
          <span>Open Dashboard</span>
          <ChevronRight className="h-3.5 w-3.5 ml-1 group-hover:translate-x-1 transition-transform" />
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Protocol Pulse Bar ───
const ProtocolPulse = ({ data }) => {
  if (!data) return null;
  const items = [
    { label: 'Hotels', value: data.network.hotels_registered, icon: Building2 },
    { label: 'Minted', value: fmt(data.halving.total_minted), icon: Coins },
    { label: 'Staked', value: fmt(data.network.total_staked), icon: Lock },
    { label: 'BTC', value: `${data.btc_treasury.amount_btc}`, icon: Bitcoin },
    { label: 'Tasks', value: data.behavioral.total_tasks, icon: Activity },
    { label: 'Claims', value: data.whistleblower.total_claims, icon: Scale },
  ];

  return (
    <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide py-1" data-testid="protocol-pulse">
      {items.map((item, i) => (
        <div key={item.label} className="flex items-center gap-3 shrink-0">
          {i > 0 && <div className="h-4 w-px bg-border shrink-0" />}
          <div className="flex items-center gap-1.5">
            <item.icon className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{item.label}:</span>
            <span className="text-xs heading-font tabular-nums font-medium">{item.value}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Main Page ───
export default function CommandCenterPage() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [halving, setHalving] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [d, h] = await Promise.all([
        getProtocolDashboard(),
        getHalvingStatus(),
      ]);
      setData(d);
      setHalving(h);
    } catch { toast.error('Failed to load command center data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const modules = data ? [
    {
      title: 'Protocol Dashboard',
      description: 'Real-time protocol health metrics, network stats, behavioral & whistleblower summaries',
      href: '/protocol-dashboard',
      icon: Gauge,
      accentColor: 'rgba(14,165,164,0.06)',
      badge: { label: `Era ${data.halving.current_era}`, color: 'text-[hsl(var(--web3-primary))] border-[hsl(var(--web3-primary))]/30' },
      stats: [
        { label: 'Hotels', value: data.network.hotels_registered },
        { label: 'Minted', value: fmt(data.halving.total_minted) },
      ],
    },
    {
      title: 'Halving Tracker',
      description: '108-hotel cycle countdown, mint curve chart, era timeline, supply tracking',
      href: '/halving',
      icon: Layers,
      accentColor: 'rgba(14,165,164,0.06)',
      badge: halving?.hotels_until_next_halving <= 20
        ? { label: `${halving.hotels_until_next_halving} spots!`, color: 'text-amber-500 border-amber-500/30' }
        : null,
      stats: [
        { label: 'Spots Left', value: halving?.hotels_until_next_halving },
        { label: 'Mint Rate', value: fmt(data.halving.current_mint_rate) },
      ],
    },
    {
      title: 'Treasury',
      description: 'BTC sovereign reserve, protocol pools, hotel treasury balances, transaction history',
      href: '/treasury',
      icon: Bitcoin,
      accentColor: 'rgba(176,141,87,0.06)',
      stats: [
        { label: 'BTC Reserve', value: `${data.btc_treasury.amount_btc}` },
        { label: 'Total Staked', value: fmt(data.network.total_staked) },
      ],
    },
    {
      title: 'Staking',
      description: 'Slash-only staking tiers, score simulation, slash/reward event history',
      href: '/staking',
      icon: Shield,
      accentColor: 'rgba(139,92,246,0.06)',
      stats: [
        { label: 'Total Staked', value: fmt(data.network.total_staked) },
        { label: 'Hotels', value: data.network.hotels_registered },
      ],
    },
    {
      title: 'Marina Escrow',
      description: 'Automated staff payroll from 1.88% booking escrow, task payments, tips, batch processing',
      href: '/marina-escrow',
      icon: Banknote,
      accentColor: 'rgba(59,130,246,0.06)',
      stats: [
        { label: 'Revenue', value: fmt(data.network.total_booking_revenue) },
        { label: 'Tasks', value: data.behavioral.total_tasks },
      ],
    },
    {
      title: 'Justice Portal',
      description: 'Whistleblower claims, asymmetric stakes, arbitration panel, verdict history',
      href: '/justice',
      icon: Scale,
      accentColor: 'rgba(239,68,68,0.06)',
      badge: data.whistleblower.pending > 0
        ? { label: `${data.whistleblower.pending} pending`, color: 'text-amber-500 border-amber-500/30' }
        : null,
      stats: [
        { label: 'Total Claims', value: data.whistleblower.total_claims },
        { label: 'Upheld', value: data.whistleblower.upheld },
      ],
    },
    {
      title: 'Register Hotel',
      description: '7-layer Anti-Sybil Gauntlet, staged minting (10%/20%/30%/40% over 90 days)',
      href: '/register-hotel',
      icon: Building2,
      accentColor: 'rgba(14,165,164,0.06)',
      stats: [
        { label: 'Onboarded', value: data.network.hotels_registered },
        { label: 'Capacity', value: '1,080' },
      ],
    },
    {
      title: 'Linen Tracking',
      description: 'RFID lifecycle management, wash cycles, room assignments, EcoProof sustainability scoring',
      href: '/linen',
      icon: Leaf,
      accentColor: 'rgba(16,185,129,0.06)',
      stats: [
        { label: 'Eco Score', value: 'Live' },
        { label: 'RFID', value: 'Active' },
      ],
    },
    {
      title: 'Account Abstraction',
      description: 'ERC-4337 Smart Accounts, Paymaster gas sponsorship, gasless one-click bookings',
      href: '/account-abstraction',
      icon: Shield,
      accentColor: 'rgba(14,165,164,0.06)',
      badge: { label: 'Phase 1', color: 'text-[hsl(var(--web3-primary))] border-[hsl(var(--web3-primary))]/30' },
      stats: [
        { label: 'EntryPoint', value: 'v0.7' },
        { label: 'Chain', value: 'Base Sepolia' },
      ],
    },
    {
      title: 'Vendor Protocol',
      description: 'Vendor SBTs, token economy, Room SBTs linking vendor products to hotel rooms for on-chain supply chain',
      href: '/vendors',
      icon: Package,
      accentColor: 'rgba(247,147,26,0.06)',
      badge: { label: 'Phase 8', color: 'text-[#F7931A] border-[#F7931A]/30' },
      stats: [
        { label: 'Vendors', value: data.network.hotels_registered > 0 ? 'Active' : '0' },
        { label: 'Room SBTs', value: 'Live' },
      ],
    },
  ] : [];

  return (
    <div className="admin-theme min-h-dvh bg-background text-foreground">
      {/* Header */}
      <div className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3">
            <div>
              <h1 className="text-xl md:text-2xl heading-font font-semibold tracking-tight" data-testid="page-title">
                Command Center
              </h1>
              <p className="text-xs text-muted-foreground">HERMES Protocol Operations</p>
            </div>
            <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
            {user && (
              <div className="flex items-center gap-2 ml-2">
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[hsl(var(--web3-surface-2))]/60" data-testid="user-badge">
                  <User className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs font-medium">{user.name}</span>
                  <Badge variant="outline" className="text-[9px] capitalize ml-1">{user.role.replace('_', ' ')}</Badge>
                  {user.hotel_id && <span className="text-[9px] text-muted-foreground">| {user.hotel_id}</span>}
                </div>
                <Button variant="ghost" size="icon" onClick={() => { logout(); navigate('/login'); }} className="h-8 w-8" data-testid="logout-btn">
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
          {/* Protocol Pulse Strip */}
          {data && (
            <div className="pb-2">
              <ProtocolPulse data={data} />
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-6 space-y-8">

        {/* Hero Banner */}
        {data && (
          <div className="relative overflow-hidden rounded-xl border bg-card p-6 md:p-8" data-testid="hero-banner">
            <div className="absolute inset-0 bg-[radial-gradient(40%_40%_at_90%_10%,rgba(14,165,164,0.08)_0%,transparent_60%)]" />
            <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center">
              <div>
                <Badge variant="outline" className="mb-3 text-xs text-[hsl(var(--web3-primary))] border-[hsl(var(--web3-primary))]/30">
                  Era {data.halving.current_era} | {fmt(data.halving.current_mint_rate)} HERMES/hotel
                </Badge>
                <h2 className="text-2xl md:text-3xl heading-font font-bold tracking-tight">
                  HERMES Protocol
                </h2>
                <p className="text-sm text-muted-foreground mt-2 max-w-lg leading-relaxed">
                  {data.network.hotels_registered} hotels onboarded. {fmt(data.halving.total_minted)} HERMES minted.
                  {' '}{halving?.hotels_until_next_halving} registration spots remaining at current mint rate before
                  the next 50% halving.
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-5xl heading-font font-black tabular-nums text-[hsl(var(--web3-primary))]">
                    {halving?.hotels_until_next_halving}
                  </p>
                  <p className="text-[10px] text-muted-foreground uppercase mt-1">Spots Left</p>
                </div>
                <div className="h-16 w-px bg-border" />
                <div className="text-center">
                  <p className="text-5xl heading-font font-black tabular-nums">
                    {data.btc_treasury.amount_btc}
                  </p>
                  <p className="text-[10px] text-muted-foreground uppercase mt-1">BTC Locked</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Module Grid */}
        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[1,2,3,4,5,6,7,8].map(i => (
              <Card key={i}><CardContent className="pt-5 pb-4 space-y-3">
                <Skeleton className="h-11 w-11 rounded-xl" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-48" />
                <div className="grid grid-cols-2 gap-2">
                  <Skeleton className="h-12" /><Skeleton className="h-12" />
                </div>
              </CardContent></Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="module-grid">
            {modules.map((mod) => (
              <ModuleCard key={mod.href} {...mod} />
            ))}
          </div>
        )}

        {/* Quick Actions */}
        {data && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="quick-actions">
            <Link to="/register-hotel" className="block">
              <Card className="group cursor-pointer hover:border-emerald-500/30 transition-colors">
                <CardContent className="pt-5 pb-4 px-5 flex items-center gap-4">
                  <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                    <Sparkles className="h-5 w-5 text-emerald-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">Register New Hotel</p>
                    <p className="text-xs text-muted-foreground">Anti-Sybil verification + staged mint</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-emerald-500 transition-colors" />
                </CardContent>
              </Card>
            </Link>
            <Link to="/justice" className="block">
              <Card className="group cursor-pointer hover:border-amber-500/30 transition-colors">
                <CardContent className="pt-5 pb-4 px-5 flex items-center gap-4">
                  <div className="h-10 w-10 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                    <Scale className="h-5 w-5 text-amber-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">File Whistleblower Claim</p>
                    <p className="text-xs text-muted-foreground">{data.whistleblower.pending} claims pending review</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-amber-500 transition-colors" />
                </CardContent>
              </Card>
            </Link>
            <Link to="/halving" className="block">
              <Card className="group cursor-pointer hover:border-[hsl(var(--web3-primary))]/30 transition-colors">
                <CardContent className="pt-5 pb-4 px-5 flex items-center gap-4">
                  <div className="h-10 w-10 rounded-lg bg-[hsl(var(--web3-primary))]/10 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                    <Timer className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">View Halving Countdown</p>
                    <p className="text-xs text-muted-foreground">{halving?.hotels_until_next_halving} hotels until mint rate drops 50%</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-[hsl(var(--web3-primary))] transition-colors" />
                </CardContent>
              </Card>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
